import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "./SiteShell";
import styles from "./SiteShell.module.css";
import Seo, { absoluteUrl, createBreadcrumbList } from "./Seo";
import blogPosts from "./blogPosts.json";
import blogTranslations from "./blogTranslations.json";
import { getLocalizedPath } from "./i18n";

const hub = blogPosts.find((post) => post.isHub);
const articles = blogPosts.filter((post) => !post.isHub);

function createArticleSchema(post, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.publishedDate,
    dateModified: post.publishedDate,
    mainEntityOfPage: absoluteUrl(path),
    image: absoluteUrl(`/${post.heroImage}`),
    author: { "@type": "Organization", name: "usmalbilder.ch" },
    publisher: { "@type": "Organization", name: "usmalbilder.ch" },
  };
}

function getPostContent(post, locale) {
  const translation = blogTranslations[locale]?.[post.id];
  if (!translation || locale === "de") {
    return post;
  }
  return { ...post, ...translation, cards: translation.cards || [], sources: translation.sources || [] };
}

function Blog({ postId, forcedLocale = "de" }) {
  const locale = forcedLocale;
  const post = getPostContent(blogPosts.find((item) => item.id === postId) || hub, locale);
  const path = getLocalizedPath(`/${post.slug}`, locale);

  if (post.isHub) {
    return (
      <>
        <Seo title={post.metaTitle} description={post.description} path={path} />
        <SiteShell locale={locale}>
          <section className={styles.pageHero}>
            <span className={styles.eyebrow}>{post.eyebrow}</span>
            <h1 className={styles.pageTitle}>{post.title}</h1>
            <p className={styles.pageDescription}>{post.heroText}</p>
          </section>
          <section className={styles.blogGrid} aria-label="Blogartikel">
            {articles.map((rawArticle) => {
              const article = getPostContent(rawArticle, locale);
              return <article className={styles.blogCard} key={article.id}>
                <img src={`/${article.heroImage}`} alt={article.heroAlt} loading="lazy" />
                <div>
                  <span className={styles.infoLabel}>{article.readTime}</span>
                  <h2 className={styles.infoTitle}>{article.title}</h2>
                  <p className={styles.infoText}>{article.description}</p>
                  <Link className={styles.textLink} to={getLocalizedPath(`/${article.slug}`, locale)}>{article.readLink || "Artikel lesen"}</Link>
                </div>
              </article>;
            })}
          </section>
        </SiteShell>
      </>
    );
  }

  return (
    <>
      <Seo
        title={post.metaTitle}
        description={post.description}
        path={path}
        image={absoluteUrl(`/${post.heroImage}`)}
        type="article"
        structuredData={[
          createArticleSchema(post, path),
          createBreadcrumbList([
            { name: "Ausmalbilder", path: "/" },
            { name: "Ausmalblog", path: "/blog" },
            { name: post.title, path },
          ]),
        ]}
      />
      <SiteShell locale="de">
        <section className={styles.pageHero}>
          <span className={styles.eyebrow}>{post.eyebrow} · {post.readTime}</span>
          <h1 className={styles.pageTitle}>{post.title}</h1>
          <p className={styles.pageDescription}>{post.heroText}</p>
          <img className={styles.blogHeroImage} src={`/${post.heroImage}`} alt={post.heroAlt} />
        </section>
        <section className={styles.articleGrid}>
          <article className={styles.copyPanel}>
            <p className={styles.copyLead}>{post.lead}</p>
            <div className={styles.copyStack}>
              {post.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
          </article>
          <aside className={styles.infoColumn}>
            {(post.cards || []).map((card) => (
              <div className={styles.infoCard} key={card.title}>
                <span className={styles.infoLabel}>{card.label}</span>
                <h2 className={styles.infoTitle}>{card.title}</h2>
                <p className={styles.infoText}>{card.text}</p>
              </div>
            ))}
            {post.sources?.length ? (
              <div className={styles.infoCard}>
                <span className={styles.infoLabel}>Weiterlesen</span>
                <h2 className={styles.infoTitle}>Quellen und Einordnung</h2>
                <ul className={styles.sourceList}>
                  {post.sources.map((source) => (
                    <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.label}: {source.title}</a></li>
                  ))}
                </ul>
              </div>
            ) : null}
          </aside>
        </section>
        <p className={styles.blogBackLink}><Link to={getLocalizedPath("/blog", locale)}>← {post.backLink || "Alle Artikel im Ausmalblog"}</Link></p>
      </SiteShell>
    </>
  );
}

export { blogPosts };
export default Blog;
